// @file slideshow export
import { captureMessage } from '@@/bits/error_tracker'
import type { PdfOrientation, PdfPageSize } from '@@/bits/export_proxy'
import { transformUrl } from '@@/bits/location'
import { getSlideshowExportFileName, SLIDESHOW_DEFAULT_EXPORT_QUERY_PARAMS } from '@@/bits/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { isUndefined, omitBy } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

/**
 * Provides a function to fetch the Google Cloud Storage URL for the slideshow document export.
 *
 * @param {Object} options - Options for the slideshow export.
 * @param {AbortSignal} [options.abortSignal] - An AbortSignal to cancel the fetch request.
 * @param {boolean} [options.removeBackground=false] - Whether to remove the background from the exported document.
 * @param {boolean} [options.removeQrCode=false] - Whether to remove the QR code from the exported document.
 * @param {PdfOrientation} [options.orientation='landscape'] - The orientation of the exported document.
 * @param {PdfPageSize} [options.size='a4'] - The page size of the exported document.
 * @param {boolean} [options.force=false] - Whether to force the export, even if the document has not changed.
 * @returns {Object} - An object with the `getSlideshowDocUrl` function.
 */
export const useSlideshowExports = ({
  abortSignal,
  removeBackground = false,
  removeQrCode = false,
  orientation = 'landscape',
  size = 'a4',
  force = false,
}: {
  abortSignal?: AbortSignal
  removeBackground?: boolean
  removeQrCode?: boolean
  orientation?: PdfOrientation
  size?: PdfPageSize
  force?: boolean
}): {
  getSlideshowDocUrl: typeof getSlideshowDocUrl
} => {
  const { title, links } = storeToRefs(useSurfaceStore())
  const baseSlideshowUrl = computed(() => links.value.slideshow_document)
  const requestUrl = computed((): string => {
    const fileName = getSlideshowExportFileName(title.value)
    return transformUrl(baseSlideshowUrl.value, {
      search: omitBy<any>(
        {
          ...SLIDESHOW_DEFAULT_EXPORT_QUERY_PARAMS,
          filename: fileName,
          remove_background: removeBackground,
          remove_qr_code: removeQrCode,
          orientation,
          page_size: size,
          force,
        },
        isUndefined,
      ),
    })
  })

  /**
   * Fetches the google cloud storage URL for the slideshow document export.
   *
   * @param {boolean} [removeBackground=false] - Whether to remove the background from the exported document.
   * @param {PdfOrientation} [orientation='landscape'] - The orientation of the exported document.
   * @returns {Promise<string>} - The URL of the exported slideshow document.
   */
  const getSlideshowDocUrl = async (): Promise<string | null> => {
    const response = await fetch(requestUrl.value, { signal: abortSignal })
    if (response.status === 200) {
      const result = await response.json()
      return result.url
    } else if (response.status === 202) {
      // this means that we don't have a URL to return to the user, and that they should wait for the email
      return null
    } else {
      captureMessage('[ExportPDFV2] Failed to fetch banksy URL for slideshow')
      throw new Error('Failed to fetch banksy URL for slideshow')
    }
  }

  return {
    getSlideshowDocUrl,
  }
}
